import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ledgerResponseSchema, transactionTypeSchema, type LedgerEntry } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';

const input = 'mt-1 block rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm';

/**
 * The audit ledger with filters (REQUIREMENTS §2.6). The storeroom sees
 * everything; a commander the traffic of the soldiers in their reach — the
 * server scopes it (story 41). Read only, by construction: nothing here can
 * change an entry, and there is no endpoint that would. The soldier filter
 * can arrive in the URL, from the soldier's page.
 */
export function LedgerPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const [type, setType] = useState('');
  const [soldierUid, setSoldierUid] = useState(params.get('soldierUid') ?? '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const path = useMemo(() => {
    const q = new URLSearchParams();
    if (type) q.set('type', type);
    if (soldierUid.trim()) q.set('soldierUid', soldierUid.trim());
    if (from) q.set('from', new Date(from).toISOString());
    // The day picked as "to" is included: exclusive bound at the next midnight.
    if (to) q.set('to', new Date(new Date(to).getTime() + 86_400_000).toISOString());
    const s = q.toString();
    return `/ledger${s ? `?${s}` : ''}`;
  }, [type, soldierUid, from, to]);

  const state = useApi(path, ledgerResponseSchema);

  const person = (uid: string | null) => (uid ? (state.kind === 'ready' ? state.data.people.find((p) => p.uid === uid)?.name : null) ?? uid : '');
  const item = (id: string | null) => {
    if (!id || state.kind !== 'ready') return '';
    const i = state.data.items.find((x) => x.id === id);
    return i ? (i18n.language.startsWith('en') ? i.nameEn : i.nameHe) : id;
  };
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language.startsWith('en') ? 'en-GB' : 'he-IL') : '');

  // Mixed-script cells: every Latin run — a quantity, a unit id — is isolated
  // in its own span, so the bidi algorithm never reorders it against the
  // Hebrew around it. `latin-inline` goes on spans, never on the cell: on a
  // cell it flips the cell's own alignment (story 37).
  const detail = (e: LedgerEntry) => {
    const bits: ReactNode[] = [];
    const itemName = item(e.catalogItemId);
    if (itemName) {
      bits.push(
        e.quantity && e.quantity > 1 ? (
          <>
            {itemName} × <span className="latin-inline">{e.quantity}</span>
          </>
        ) : (
          itemName
        ),
      );
    }
    if (e.inventoryUnitId) bits.push(<span className="latin-inline text-xs text-slate-500">{e.inventoryUnitId}</span>);
    if (e.notes) bits.push(e.notes);
    return bits.map((b, i) => (
      <span key={i}>
        {i > 0 && ' · '}
        {b}
      </span>
    ));
  };

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="ledger-page">
      <h2 className="text-lg font-semibold text-slate-900">{t('ledger.title')}</h2>
      <p className="mt-1 text-sm text-slate-500">{t('ledger.hint')}</p>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600">
          {t('ledger.type')}
          <select className={input} value={type} onChange={(e) => setType(e.target.value)} data-testid="ledger-type">
            <option value="">{t('ledger.anyType')}</option>
            {transactionTypeSchema.options.map((tt) => (
              <option key={tt} value={tt}>{t(`ledger.types.${tt}`)}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          {t('ledger.soldier')}
          <input className={`${input} latin-inline w-40`} value={soldierUid} onChange={(e) => setSoldierUid(e.target.value)} placeholder="uid" data-testid="ledger-soldier" />
        </label>
        <label className="text-xs text-slate-600">
          {t('ledger.from')}
          <input type="date" className={`${input} latin-inline`} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ledger-from" />
        </label>
        <label className="text-xs text-slate-600">
          {t('ledger.to')}
          <input type="date" className={`${input} latin-inline`} value={to} onChange={(e) => setTo(e.target.value)} data-testid="ledger-to" />
        </label>
      </div>

      {state.kind === 'loading' && <p className="mt-3 text-slate-500">{t('home.loading')}</p>}
      {state.kind === 'error' && <div className="mt-3"><ApiError code={state.code} requestId={state.requestId} /></div>}
      {state.kind === 'ready' && (
        <>
          <p className="mt-3 text-xs text-slate-500" data-testid="ledger-count">{t('ledger.count', { count: state.data.entries.length })}</p>
          {state.data.entries.length === 0 && <p className="mt-1 text-sm text-slate-500" data-testid="ledger-empty">{t('ledger.empty')}</p>}
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-start text-sm">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="py-1 pe-3 text-start font-medium">{t('ledger.when')}</th>
                  <th className="py-1 pe-3 text-start font-medium">{t('ledger.type')}</th>
                  <th className="py-1 pe-3 text-start font-medium">{t('ledger.actor')}</th>
                  <th className="py-1 pe-3 text-start font-medium">{t('ledger.soldier')}</th>
                  <th className="py-1 text-start font-medium">{t('ledger.detail')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {state.data.entries.map((e) => (
                  <tr key={e.id} data-testid="ledger-row" data-type={e.type}>
                    <td className="py-1.5 pe-3 whitespace-nowrap text-xs text-slate-500">
                      <span className="latin-inline">{when(e.createdAt)}</span>
                    </td>
                    <td className="py-1.5 pe-3 whitespace-nowrap">{t(`ledger.types.${e.type}`)}</td>
                    <td className="py-1.5 pe-3 whitespace-nowrap">{person(e.actorUid)}</td>
                    <td className="py-1.5 pe-3 whitespace-nowrap">
                      {e.soldierUid && (
                        <button type="button" onClick={() => setSoldierUid(e.soldierUid ?? '')} className="underline-offset-4 hover:underline" data-testid="ledger-filter-soldier">
                          {person(e.soldierUid)}
                        </button>
                      )}
                    </td>
                    <td className="py-1.5 text-slate-700">
                      {detail(e)}
                      {e.formId && (
                        <Link to={`/forms/${e.formId}`} className="ms-2 text-xs text-slate-500 underline-offset-4 hover:underline">
                          {t('ledger.openForm')}
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
